"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { getFormula, parseDataLocationPath, schemaWithTitle } from '@membranehq/sdk';
import { ComboBox } from '../../ComboBox/ComboBox.mjs';
import { DataBuilderForm } from '../../DataBuilder/Form.mjs';
import { FormComponentsProvider } from '../../DataBuilder/form-components-context.mjs';
import { formulaComponentMapping } from '../../DataBuilder/Formula/index.mjs';
import { useMembrane } from '../../../contexts/use-integration-app.mjs';
import { toHeaderCase } from 'js-convert-case';
import useSWR from 'swr';
import { makeDataField } from '../../DataBuilder/index.mjs';
import '../../DataBuilderUI/FieldControlPanel/index.mjs';
import '../../DataBuilderUI/ActionBar.mjs';
import { Cell } from '../../DataBuilderUI/Cell.mjs';
import 'clsx';
import '../../DataBuilderUI/styles.module.css.mjs';
import { Row } from '../../DataBuilderUI/Row.mjs';
import '../../DataBuilderUI/Title.mjs';
import { useIntegrationElementContext } from '../use-integration-element.mjs';
import { FormulaComponentsProvider } from '../../DataBuilder/Formula/formula-components-context.mjs';

function DataCollectionSelect({
  value,
  onChange,
  variablesSchema,
  readOnly
}) {
  const { integrationId } = useIntegrationElementContext();
  const client = useMembrane();
  let currentKey = value?.collectionKey;
  let currentParameters = value?.collectionParameters;
  const isDynamicLocation = !!getFormula(currentKey);
  if (value?.path && !currentKey) {
    const pointer = parseDataLocationPath(value?.path);
    currentKey = pointer?.key;
    currentParameters = pointer?.parameters;
  }
  const { data: location } = useSWR(
    currentKey && !isDynamicLocation ? `/integrations/${integrationId}/data/${currentKey}` : null,
    (uri) => client.get(uri)
  );
  const { data: locations = [], isLoading } = useSWR(`/integrations/${integrationId}/data`, (uri) => client.get(uri));
  const options = [];
  const filtered = [...locations ?? []];
  filtered.map((location2) => {
    options.push({
      value: location2.key,
      label: location2.name?.toString() ?? toHeaderCase(location2.key)
    });
  });
  function handleKeyChange(key) {
    if (key !== currentKey) {
      return onChange({
        collectionKey: key,
        collectionParameters: void 0,
        // Backward compat
        // Remove it when we migrated all the configs to not use path/defaultPath
        path: void 0,
        defaultPath: void 0
      });
    }
  }
  function handleParametersChange(parameters) {
    const patch = currentKey ? {
      collectionKey: currentKey,
      collectionParameters: parameters
    } : {
      collectionKey: void 0,
      collectionParameters: void 0
    };
    onChange({
      ...patch,
      // Backward compat
      // Remove it when we migrated all the configs to not use path/defaultPath
      path: void 0,
      defaultPath: void 0
    });
  }
  const showParameters = location?.parametersSchema || isDynamicLocation;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(Row, { children: [
      /* @__PURE__ */ jsx(Cell, { type: "name", children: "Data Collection" }),
      /* @__PURE__ */ jsx(Cell, { type: "combobox", disabled: isLoading, span: "grow", children: /* @__PURE__ */ jsx(FormComponentsProvider, { DataBuilderForm, children: /* @__PURE__ */ jsx(FormulaComponentsProvider, { mapping: formulaComponentMapping, children: /* @__PURE__ */ jsx(
        ComboBox,
        {
          value: currentKey,
          onChange: handleKeyChange,
          disabled: isLoading || readOnly,
          options: options?.length > 0 ? options : void 0,
          schema: { type: "string" },
          variablesSchema
        }
      ) }) }) })
    ] }),
    showParameters && /* @__PURE__ */ jsx("div", { className: "mt-2", children: /* @__PURE__ */ jsx(
      DataBuilderForm,
      {
        readOnly,
        onChange: handleParametersChange,
        field: makeDataField({
          schema: schemaWithTitle(
            location ? location.parametersSchema : {
              type: "object",
              additionalProperties: true
            },
            "Parameters"
          ),
          value: currentParameters,
          variablesSchema
        })
      }
    ) })
  ] });
}

export { DataCollectionSelect };
